import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import { CreateUserGroupPayload, UserGroup } from "../types";
import { queryKeys } from "./queryKeys";

const baseUrl = "/api/user-groups";

const onGetUserGroups = async (): Promise<UserGroup[]> => {
  const { data } = await axios.get(baseUrl);
  return data;
};

export const useUserGroups = (enabled: boolean = true) =>
  useQuery<UserGroup[]>({
    queryKey: [queryKeys.USER_GROUPS],
    queryFn: onGetUserGroups,
    enabled,
  });

const onGetMyUserGroups = async (): Promise<UserGroup[]> => {
  const { data } = await axios.get(`${baseUrl}/me`);
  return data;
};

export const useMyUserGroups = (enabled: boolean = true) =>
  useQuery<UserGroup[]>({
    queryKey: [queryKeys.USER_GROUPS, "me"],
    queryFn: onGetMyUserGroups,
    enabled,
  });

const useGroupMutation = <TArgs>(
  mutationFn: (args: TArgs) => Promise<UserGroup | void>,
  onSuccess?: () => void,
  onError?: (error: any) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.USER_GROUPS] });
      onSuccess?.();
    },
    onError,
  });
};

export const useCreateUserGroup = (
  onSuccess?: () => void,
  onError?: (error: any) => void
) =>
  useGroupMutation<CreateUserGroupPayload>(
    async (body) => (await axios.post(baseUrl, body)).data,
    onSuccess,
    onError
  );

export const useUpdateUserGroup = (
  onSuccess?: () => void,
  onError?: (error: any) => void
) =>
  useGroupMutation<{ id: string; name: string }>(
    async ({ id, name }) =>
      (await axios.put(`${baseUrl}/${id}`, { name })).data,
    onSuccess,
    onError
  );

export const useDeleteUserGroup = (
  onSuccess?: () => void,
  onError?: (error: any) => void
) =>
  useGroupMutation<string>(
    async (id) => {
      await axios.delete(`${baseUrl}/${id}`);
    },
    onSuccess,
    onError
  );

export const useAddGroupMembers = (
  onSuccess?: () => void,
  onError?: (error: any) => void
) =>
  useGroupMutation<{ id: string; userIds: string[] }>(
    async ({ id, userIds }) =>
      (await axios.post(`${baseUrl}/${id}/members`, { userIds })).data,
    onSuccess,
    onError
  );

export const useRemoveGroupMember = (
  onSuccess?: () => void,
  onError?: (error: any) => void
) =>
  useGroupMutation<{ id: string; userId: string }>(
    async ({ id, userId }) =>
      (await axios.delete(`${baseUrl}/${id}/members/${userId}`)).data,
    onSuccess,
    onError
  );
